import { Suspense, lazy, useEffect, useState, useMemo } from 'react';
import './Countdown.css';

// Lazy-load Three.js only when a 3D model is needed
const Car3DViewer = lazy(() => import('./DriverProfileCar3D'));

/* ---------- Types ---------- */

interface ResolvedCar {
  id: string;
  car_number: string;
  car_name: string | null;
  manufacturer: string | null;
  model_3d_url: string | null;
  side_image_url: string | null;
}

interface ResolvedSponsor {
  id: string;
  name: string;
  logo_light_url: string;
  logo_dark_url: string | null;
}

interface CountdownProps {
  orgName: string;
  logoUrl: string | null;
  primaryColor: string;
  eventName: string | null;
  timezone: string | null;
  config: Record<string, unknown> | null;
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
}

/* ---------- Helpers ---------- */

function padTwo(n: number): string {
  return n.toString().padStart(2, '0');
}

/**
 * Calculate time remaining until a target date.
 * The targetTime string from config is treated as a local datetime
 * in the specified timezone (or event timezone).
 */
function getTimeRemaining(targetTime: string, tz: string | null): TimeRemaining {
  const expired = { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };

  if (!targetTime) return expired;

  try {
    let targetMs: number;

    if (tz) {
      // Get current time in the target timezone
      const nowInTz = new Date().toLocaleString('en-US', { timeZone: tz });
      const nowMs = new Date(nowInTz).getTime();

      // Parse target as a date (it's in the same tz conceptually)
      targetMs = new Date(targetTime).getTime();
      const diff = targetMs - nowMs;

      if (diff <= 0) return expired;

      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
        expired: false,
      };
    }

    // No timezone — treat as local time
    targetMs = new Date(targetTime).getTime();
    const diff = targetMs - Date.now();

    if (diff <= 0) return expired;

    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
      expired: false,
    };
  } catch {
    return expired;
  }
}

/* ---------- Component ---------- */

export default function Countdown({
  orgName,
  logoUrl,
  eventName,
  timezone,
  config,
}: CountdownProps) {
  const title = (config?.title as string) ?? '';
  const targetTime = (config?.targetTime as string) ?? '';
  const expiredMessage = (config?.expiredMessage as string) || 'LIVE NOW';

  // Resolved data from RPC (same pattern as DriverProfile)
  const raceCar = (config?._resolved_car as ResolvedCar) ?? null;
  const sponsors = (config?._resolved_sponsors as ResolvedSponsor[]) ?? [];
  const carModelUrl = raceCar?.model_3d_url ?? (config?.carModelUrl as string) ?? null;
  const has3D = !!carModelUrl;
  const hasCarImage = !!raceCar?.side_image_url;

  // Sponsor scroll — duplicate enough for seamless loop
  const minItems = 24;
  const repeatCount = sponsors.length > 0 ? Math.max(2, Math.ceil(minItems / sponsors.length) * 2) : 0;
  const sponsorTrackItems = Array.from({ length: repeatCount }, () => sponsors).flat();

  const [remaining, setRemaining] = useState<TimeRemaining>(() =>
    getTimeRemaining(targetTime, timezone)
  );

  const stableTargetTime = useMemo(() => targetTime, [targetTime]);
  const stableTimezone = useMemo(() => timezone, [timezone]);

  useEffect(() => {
    if (!stableTargetTime) return;

    const tick = () => {
      setRemaining(getTimeRemaining(stableTargetTime, stableTimezone));
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [stableTargetTime, stableTimezone]);

  if (!title && !targetTime) {
    return (
      <div className="countdown-screen countdown-screen--empty">
        <p className="countdown-screen__empty-text">No countdown configured</p>
      </div>
    );
  }

  return (
    <div className="countdown-screen">
      {/* ── 3D Car background ── */}
      {has3D && (
        <div className="countdown-screen__car-3d">
          <Suspense fallback={null}>
            <Car3DViewer modelUrl={carModelUrl!} />
          </Suspense>
        </div>
      )}
      {!has3D && hasCarImage && (
        <div className="countdown-screen__car-image">
          <img src={raceCar!.side_image_url!} alt="Race car" draggable={false} />
        </div>
      )}

      {/* ── Vignette over car ── */}
      {(has3D || hasCarImage) && <div className="countdown-screen__vignette" />}

      {/* Background glow */}
      <div className="countdown-screen__glow" />

      <div className="countdown-screen__container">
        {/* Title */}
        {title && (
          <div className="countdown-screen__title">{title}</div>
        )}

        {/* Countdown digits or expired message */}
        {remaining.expired ? (
          <div className="countdown-screen__expired">{expiredMessage}</div>
        ) : (
          <div className="countdown-screen__digits">
            {remaining.days > 0 && (
              <>
                <div className="countdown-screen__block">
                  <div className="countdown-screen__value">{remaining.days}</div>
                  <div className="countdown-screen__label">Days</div>
                </div>
                <div className="countdown-screen__separator">:</div>
              </>
            )}

            <div className="countdown-screen__block">
              <div className="countdown-screen__value">{padTwo(remaining.hours)}</div>
              <div className="countdown-screen__label">Hours</div>
            </div>
            <div className="countdown-screen__separator">:</div>

            <div className="countdown-screen__block">
              <div className="countdown-screen__value">{padTwo(remaining.minutes)}</div>
              <div className="countdown-screen__label">Minutes</div>
            </div>
            <div className="countdown-screen__separator">:</div>

            <div className="countdown-screen__block">
              <div className="countdown-screen__value">{padTwo(remaining.seconds)}</div>
              <div className="countdown-screen__label">Seconds</div>
            </div>
          </div>
        )}

        {/* Event name below */}
        {eventName && (
          <div className="countdown-screen__event">{eventName}</div>
        )}
      </div>

      {/* Sponsor bar — bottom, scrolling */}
      {sponsors.length > 0 && (
        <div className="countdown-screen__sponsor-bar">
          <div className="countdown-screen__sponsor-track">
            {sponsorTrackItems.map((s, i) => (
              <img
                key={`${s.id}-${i}`}
                className="countdown-screen__sponsor-logo"
                src={s.logo_dark_url ?? s.logo_light_url}
                alt={s.name}
                draggable={false}
              />
            ))}
          </div>
        </div>
      )}

      {/* Org logo — bottom-right */}
      {logoUrl && (
        <img
          className="countdown-screen__org-logo"
          src={logoUrl}
          alt={`${orgName} logo`}
          draggable={false}
        />
      )}
    </div>
  );
}
